/**
 * A boundary around the routed screen.
 *
 * Without one, a single bad render anywhere in the tree unmounts the whole
 * application and leaves a supervisor staring at a blank page with no way back
 * — which is precisely what a plant floor cannot afford at 2am. The shell,
 * navigation and sign-out survive; only the failed screen is replaced.
 */

import { Component, type ErrorInfo, type ReactNode } from "react";

interface Props {
  /** Changing this resets the boundary — navigating away should clear it. */
  resetKey: string;
  children: ReactNode;
}

interface State {
  error: Error | null;
  lastKey: string;
}

export class ErrorBoundary extends Component<Props, State> {
  state: State = { error: null, lastKey: this.props.resetKey };

  static getDerivedStateFromProps(props: Props, state: State): State | null {
    // Navigating to another screen clears the failure rather than stranding
    // the user on it.
    if (props.resetKey !== state.lastKey) {
      return { error: null, lastKey: props.resetKey };
    }
    return null;
  }

  static getDerivedStateFromError(error: Error): Partial<State> {
    return { error };
  }

  componentDidCatch(error: Error, info: ErrorInfo): void {
    // Kept in the console rather than swallowed — a screen that fails
    // silently is worse than one that fails loudly.
    console.error("Screen failed to render:", error, info.componentStack);
  }

  render() {
    if (!this.state.error) return this.props.children;

    return (
      <div className="state state--empty">
        <span className="state__glyph" aria-hidden="true">
          ⚠
        </span>
        <h3>This screen could not be drawn</h3>
        <p>
          Nothing has been changed and no data is lost — this is a display
          fault, not a data one. Everything else still works; pick another
          screen from the left, or reload to try again.
        </p>
        <p className="page__note">{this.state.error.message}</p>
        <button
          className="btn btn--auto"
          onClick={() => this.setState({ error: null })}
        >
          Try again
        </button>
      </div>
    );
  }
}
